import React, { useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import TickWithBorderIcon from 'components/Icons/TickWithBorderIcon';
import {
  useForecastFeedbackModalOpenState,
  useForecastFeedbackFormSubmissionSuccess,
} from 'utils/forecastFeedbackState';
import { useRouter } from 'next/router';
import { useUnmount } from 'react-use';

import styles from '../ForecastFeedbackForms.module.scss';

interface Props {
  buttonText?: string;
  heading: string;
  linkHref?: string;
  message?: string | React.ReactNode;
  onSubmit: () => void;
}

const Success: React.FC<Props> = ({
  buttonText = 'Done',
  heading,
  linkHref,
  message,
  onSubmit,
}) => {
  const router = useRouter();
  const [, setIsModalOpen] = useForecastFeedbackModalOpenState();
  const [, setFormSubmissionSuccess] = useForecastFeedbackFormSubmissionSuccess();
  const handleDoneClick = useCallback(() => {
    onSubmit();
    setIsModalOpen(false);
    if (linkHref) {
      router.push(linkHref);
    }
  }, [linkHref, onSubmit, router, setIsModalOpen]);
  useUnmount(() => {
    setFormSubmissionSuccess({ status: false });
  });
  return (
    <Box data-testid="forecast-feedback-success-wrapper">
      <Box className={styles.statusIconWrapper}>
        <TickWithBorderIcon className={styles.tickIcon} />
      </Box>
      <Typography
        color="text.brandSecondary"
        component="h2"
        variant="title3"
        className={styles.statusHeading}
        data-testid="forecast-feedback-success-heading"
      >
        {heading}
      </Typography>
      <Typography
        color="text.primary"
        component="p"
        variant="body1"
        className={styles.statusMessage}
        data-testid="forecast-feedback-success-message"
      >
        {message}
      </Typography>
      <Box className={styles.statusButtonWrapper}>
        <Button
          variant="primary"
          onClick={handleDoneClick}
          className={styles.doneButton}
          disableFocusRipple
          disableRipple
          disableTouchRipple
          data-testid="done-button"
        >
          {buttonText}
        </Button>
      </Box>
    </Box>
  );
};

export default Success;
